import { GridLayout, MetricsResponse } from 'API';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highcharts3d from 'highcharts/highcharts-3d';
import { memo, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import HCExporting from 'highcharts/modules/exporting';

// Initialize Highcharts modules
HCExporting(Highcharts);
Highcharts.SVGRenderer.prototype.symbols['c-rect'] = function (x: any, y: any, w: any, h: any) {
  return ['M', x, y + h / 2, 'L', x + w, y + h / 2];
};

highcharts3d(Highcharts);

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding-top: 50px;
`;

interface CustomReportResponsiveGridChartProps {
  joinedData: any;
  chartMetrics: MetricsResponse[];
  title?: string;
  layoutItem: GridLayout;
}

const colorArray = [
  '#7D98AB',
  '#A9C8C0',
  '#DBBC8E',
  '#C6ADAE',
  '#698296',
  '#A3B6C4',
  '#E5D0AF',
  '#C2D8D3',
  '#95A7B5',
  '#AE8A8C',
];

const CustomReportResponsiveGridChart = (props: CustomReportResponsiveGridChartProps) => {
  const { joinedData, chartMetrics, title /*  = '' */, layoutItem } = props;
  // console.log("'RENDER CustomReportResponsiveGridChart", props);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const theme = useTheme();
  useEffect(() => {
    // Find the string property to use as categories
    const firstDataItem = joinedData?.[0] || {};
    const stringPropertyKey = Object.keys(firstDataItem).find(
      (key) => typeof firstDataItem[key] === 'string' && firstDataItem[key] !== null,
    );

    if (!stringPropertyKey || !joinedData?.length || !chartMetrics?.length) {
      return;
    } // Extract categories from the string property
    const categories = joinedData.map((item: Record<string, any>) => item[stringPropertyKey]);

    // Find numeric properties to create series
    const numericProperties = Object.keys(firstDataItem).filter(
      (key) => typeof firstDataItem[key] === 'number' || firstDataItem[key] === null,
    );

    // Create series for each metric found in chartMetrics and present in data
    const series: Highcharts.SeriesOptionsType[] = [];

    chartMetrics.forEach((metric, index) => {
      // Find the matching property in the data
      const matchingProperty = numericProperties.find((prop) => prop === metric.metric);

      if (matchingProperty) {
        // Create series data
        const seriesData = joinedData.map((item: Record<string, any>) =>
          item[matchingProperty] === null ? 0 : item[matchingProperty],
        );

        // Determine chart type
        let seriesType: string = 'column';
        if (metric.chartType === 'pie') {
          seriesType = 'pie';
        } else if (metric.chartType === 'line') {
          seriesType = 'line';
        } else if (metric.chartType === 'stacked') {
          seriesType = 'column'; // We'll handle stacking in plotOptions
        }

        // Create the formatted data based on chart type
        const formattedData =
          seriesType === 'pie'
            ? categories.map((category: string, i: number) => ({
                name: category,
                y: seriesData[i] as number,
              }))
            : seriesData;

        series.push({
          name: matchingProperty,
          data: formattedData,
          type: seriesType as any,
          color: colorArray[index % colorArray.length],
          yAxis: metric.axis || 0,
        });
      }
    });

    // Check if any chart should be stacked
    const hasStackedChart = chartMetrics.some((metric) => metric.chartType === 'stacked');

    // Create chart options
    const options: Highcharts.Options = {
      chart: {
        backgroundColor: 'white',
        zooming: {
          type: 'xy',
        },
        style: {
          fontFamily: 'fontRegular',
        },
        width: (window.innerWidth / 9) * (layoutItem.GridPosition?.w ?? 1),
        height: (layoutItem.GridPosition?.h ?? 1) * 80 + (layoutItem.GridPosition?.h ?? 1 - 1) * 11 - 100,
        reflow: true,
        animation: false,
      },

      title: {
        text: title || '',
        // align: 'left',
        style: {
          fontSize: '14px',
          color: theme.primaryGray,
          fontFamily: 'fontRegular',
        },
      },

      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen'],
            symbolFill: theme.primaryGray,
            symbol: 'menu', // Možete promijeniti ikonu (menu, menuball, rectangle, diamond...)
            // symbolFill: theme.primaryGray,
            symbolStroke: theme.border,
          },
        },
        menuStyle: {
          backgroundColor: 'white',
          border: `1px solid ${theme.border}`,
          borderRadius: '4px',
          padding: '4px 0',
          boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.15)',
        },
      } as any,
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          useHTML: true,
          style: {
            fontSize: '11px',
            color: theme.primaryGray,
          },
        },
      },
      yAxis: chartMetrics.map((metric, index) => ({
        min: 0,
        title: {
          text: metric.metric || '',
          style: {
            color: colorArray[index % colorArray.length],
            fontSize: '11px',
          },
          enabled: false,
        },

        labels: {
          enabled: false,
          style: {
            color: colorArray[index % colorArray.length],
            fontSize: '10px',
          },
        },
        gridLineWidth: 0,
        opposite: index % 2 === 1, // Every second axis on the right side
      })),
      tooltip: {
        shared: !series.some((s) => s.type === 'pie'),
        useHTML: true,
        formatter: function () {
          if (this.point && (this.point as any).name) {
            // For pie charts
            const value = (this.point as any).y;
            // Calculate total for pie charts using the series data points
            const total = (this.series.points || []).reduce((sum, p) => sum + ((p as any).y || 0), 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `<b>${(this.point as any).name}</b><br>${this.series.name}: ${value} (${percentage}%)`;
          } else {
            // For other chart types
            const points = this.points || [this];
            return (
              `<b>${this.x}</b><br>` +
              points
                .map((point) => {
                  // Get all values for this series to calculate percentage
                  const seriesValues = (point.series.points || []).map((p) => (p as any).y || 0);
                  const total = seriesValues.reduce((sum, val) => sum + val, 0);
                  const percentage = (((point.y as number) / total) * 100).toFixed(1);
                  return `${point.series.name}: ${point.y} (${percentage}%)`;
                })
                .join('<br>')
            );
          }
        },
        style: {
          fontSize: '11px',
          color: theme.primaryGray,
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
          stacking: hasStackedChart ? 'normal' : undefined,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return Intl.NumberFormat('en-DK', { notation: 'compact' }).format(this.y as number);
            },
            style: {
              fontSize: '10px',
              color: theme.primaryGray,
              textOutline: 'transparent',
            },
          },
        },
        line: {
          marker: {
            radius: 3,
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              return Intl.NumberFormat('en-DK', { notation: 'compact' }).format(this.y as number);
            },
            style: {
              fontSize: '10px',
              textOutline: 'none',
            },
          },
        },
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            distance: 10,
            enabled: true,
            formatter: function (this: any) {
              // let cat = categ;
              const a = this.y;
              const b = this.series.processedYData.reduce((a: any, b: any) => a + b, 0);
              //  const x = this.point.x;
              const test = this?.y;
              if ((test as number) > 0) {
                return (
                  /* cat[x] + ":<br>" + */ Intl.NumberFormat('en-DK', { notation: 'compact' }).format(this.y) +
                  ' (' +
                  ((a / b) * 100).toFixed(0) +
                  '%)'
                );
              }
            },
            style: {
              fontSize: '10px',
              color: theme.primaryGray,
              textOutline: 'transparent',
            },
          },
          colors: colorArray,
          showInLegend: true,
        },
      },
      series,
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
        itemStyle: {
          fontSize: '11px',
          fontWeight: 'normal',
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
              },
            },
          },
        ],
      },
    };

    setChartOptions(options);
  }, [joinedData, chartMetrics, title]);

  useEffect(() => {
    if (layoutItem && layoutItem?.Type === 'chart' && chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      // Reflow the chart to fit the new layout item size
      const calculatedHeight =
        (layoutItem.GridPosition?.h ?? 1) * 80 + (layoutItem.GridPosition?.h ?? 1 - 1) * 11 - 100;
      // get window width and height
      const windowWidth = window.innerWidth;
      const calculatedWidth = (windowWidth / 9) * (layoutItem.GridPosition?.w ?? 1);
      chart.setSize(calculatedWidth, calculatedHeight);
      //   chart.reflow();
    }
  }, [layoutItem]);

  console.log('WHAT WENT WRONG WITH THIS CHART', chartOptions, joinedData, chartMetrics);

  return (
    <ChartContainer>
      {chartOptions && (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
          containerProps={{
            style: {
              height: '100%',
              width: '100%',
            },
          }}
        />
      )}
    </ChartContainer>
  );
};

export default memo(CustomReportResponsiveGridChart);
